import { saveAs } from 'file-saver-es';
import { mergeMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { DomainDataService, RouteFacade, RouterSelectors } from '@mitsubishi/core';
import { RequestLogRequest, RequestLogService, StudyRequestLogScope } from '@mitsubishi/shared/data-access';
import { MiscUtils } from '@mitsubishi/utils';

import { Navigate } from '@ngxs/router-plugin';
import { Action, State, StateContext, Store } from '@ngxs/store';

import { AddGroupModelsProgress, StudyAddUnitToQuoteRequest } from '../../models';
import { StudyService } from '../../services';
import { StudyActions } from './study.actions';
import { PROJECT_STUDY_STATE_TOKEN, StudyStateModel } from './study.model';

const defaultState: StudyStateModel = {
  modelsAdditionProgress: [],
};

@State<StudyStateModel>({
  name: PROJECT_STUDY_STATE_TOKEN,
  defaults: defaultState,
})
@Injectable()
export class ProjectStudyState {
  constructor(
    private store: Store,
    private studyService: StudyService,
    private requestLogService: RequestLogService,
    private routeFacade: RouteFacade,
    private domainDataService: DomainDataService
  ) {}

  @Action(StudyActions.Open)
  open(ctx: StateContext<string>, { studyId }: StudyActions.Open) {
    const projectId = this.currentProjectId;
    return ctx.dispatch(new Navigate(['project', projectId, 'study', studyId]));
  }

  @Action(StudyActions.Create)
  create(ctx: StateContext<string>, { certified, systemType, organizationId }: StudyActions.Create) {
    const projectId = this.currentProjectId;
    return this.studyService
      .createStudy(projectId, certified, organizationId, systemType)
      .pipe(mergeMap(response => ctx.dispatch(new StudyActions.Open(response.uuid))));
  }

  @Action(StudyActions.Delete)
  delete(ctx: StateContext<string>) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService
      .deleteStudy(projectId, studyId)
      .pipe(mergeMap(() => ctx.dispatch(new Navigate(['project', projectId]))));
  }

  @Action(StudyActions.UpdateName)
  updateName(ctx: StateContext<string>, { name }: StudyActions.UpdateName) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.updateStudyName(projectId, studyId, name);
  }

  @Action(StudyActions.UpdateNote)
  updateNote(ctx: StateContext<string>, { note }: StudyActions.UpdateNote) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.updateStudyNote(projectId, studyId, note);
  }

  @Action(StudyActions.UpdatePerformanceData)
  updateOptimalValues(
    ctx: StateContext<string>,
    { subcategoryId, workingConditionId, performanceData, requestTraceId }: StudyActions.UpdatePerformanceData
  ) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;

    return this.studyService.updatePerformanceData(
      projectId,
      studyId,
      subcategoryId,
      workingConditionId,
      performanceData,
      false,
      requestTraceId
    );
  }

  @Action(StudyActions.CreateUnit)
  createUnit(ctx: StateContext<string>, { modelSearchUnitId, requestTraceId }: StudyActions.CreateUnit) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;

    return this.studyService.createUnit(projectId, studyId, modelSearchUnitId, requestTraceId);
  }

  @Action(StudyActions.DeleteUnit)
  deleteUnit(ctx: StateContext<string>, { subcategoryId, unitId }: StudyActions.DeleteUnit) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.deleteUnit(projectId, studyId, subcategoryId, unitId);
  }

  @Action(StudyActions.ToggleGoodForSelection)
  toggleGoodForSelection(
    ctx: StateContext<string>,
    { subcategoryId, unitId, performanceId }: StudyActions.ToggleGoodForSelection
  ) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.toggleGoodForSelection(projectId, studyId, subcategoryId, unitId, performanceId);
  }

  @Action(StudyActions.UpdateWorkingCondition)
  UpdateWorkingCondition(
    ctx: StateContext<string>,
    { name, subcategoryId, workingConditionId }: StudyActions.UpdateWorkingCondition
  ) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.updateWorkingCondition(projectId, studyId, subcategoryId, workingConditionId, name);
  }

  @Action(StudyActions.CreateWorkingCondition)
  createWorkingCondition(
    ctx: StateContext<string>,
    { subcategoryId, requestTraceId }: StudyActions.CreateWorkingCondition
  ) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;

    return this.studyService.createWorkingCondition(projectId, studyId, subcategoryId, requestTraceId);
  }

  @Action(StudyActions.DeleteWorkingCondition)
  deleteWorkingCondition(
    ctx: StateContext<string>,
    { subcategoryId, workingConditionId }: StudyActions.DeleteWorkingCondition
  ) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.deleteWorkingCondition(projectId, studyId, subcategoryId, workingConditionId);
  }

  @Action(StudyActions.AddUnitToQuote)
  addUnitToQuote(ctx: StateContext<string>, { selection, unitUuid, performanceId }: StudyActions.AddUnitToQuote) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    const request: StudyAddUnitToQuoteRequest = {
      alternativeId: selection.base.uuid,
      studyId: studyId,
      studyUnitId: unitUuid,
      performanceId: performanceId,
    };
    return this.studyService.addUnitToQuote(projectId, selection.details.uuid, request);
  }

  @Action(StudyActions.UpdateStudyUnitNote)
  updateStudyUnitNote(
    ctx: StateContext<string>,
    { subcategoryId, unitUuid, performanceId, note }: StudyActions.UpdateStudyUnitNote
  ) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.updateStudyUnitNote(projectId, studyId, subcategoryId, unitUuid, performanceId, note);
  }

  @Action(StudyActions.UpdateAddBleenGroupProgress)
  updateAddBleenGroupProgress(
    ctx: StateContext<StudyStateModel>,
    { bleenGroupId, progress }: StudyActions.UpdateAddBleenGroupProgress
  ) {
    const state = ctx.getState();
    const currentGroup = state.modelsAdditionProgress.find(group => group.bleenGroupId === bleenGroupId);
    switch (progress) {
      case 0:
        ctx.patchState({
          modelsAdditionProgress: [...state.modelsAdditionProgress, { bleenGroupId, progress }],
        });
        break;
      case 100:
        ctx.patchState({
          modelsAdditionProgress: state.modelsAdditionProgress.map(group =>
            group.bleenGroupId === bleenGroupId ? { bleenGroupId, progress } : group
          ),
        });
        // wait 2 seconds before removing the progress so the user can see the progress has finished
        setTimeout(() => {
          ctx.patchState({
            modelsAdditionProgress: state.modelsAdditionProgress.filter(group => group.bleenGroupId !== bleenGroupId),
          });
        }, 2000);
        break;
      default:
        if (currentGroup) {
          ctx.patchState({
            modelsAdditionProgress: state.modelsAdditionProgress.map(group =>
              group.bleenGroupId === bleenGroupId ? { bleenGroupId, progress } : group
            ),
          });
        } else {
          ctx.patchState({
            modelsAdditionProgress: [...state.modelsAdditionProgress, { bleenGroupId, progress }],
          });
        }
    }
  }

  @Action(StudyActions.ClearState)
  clearState(ctx: StateContext<StudyStateModel>) {
    ctx.patchState({
      modelsAdditionProgress: [],
    });
  }

  @Action(StudyActions.DeleteSubcategory)
  deleteSubcategory(ctx: StateContext<string>, { subcategoryId }: StudyActions.DeleteSubcategory) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.deleteSubcategory(projectId, studyId, subcategoryId);
  }

  @Action(StudyActions.GotoUnitConf)
  gotoUnitConf(
    ctx: StateContext<string>,
    { studyId, unitId, performanceId, workingConditionId, fromStudy }: StudyActions.GotoUnitConf
  ) {
    const projectId = this.currentProjectId;
    this.routeFacade.setPreviousUrlByKey('study-unit-configuration');
    return ctx.dispatch(
      fromStudy
        ? new Navigate([
            'project',
            projectId,
            'study',
            studyId,
            'unit',
            unitId,
            'study-configuration',
            'wc',
            workingConditionId,
            'performance',
            performanceId,
          ])
        : new Navigate([
            'project',
            projectId,
            'study',
            studyId,
            'unit',
            unitId,
            'study-selection-configuration',
            'wc',
            workingConditionId,
            'performance',
            performanceId,
          ])
    );
  }

  @Action(StudyActions.DownloadPerformanceRequestLog)
  downloadRequest(
    ctx: StateContext<string>,
    { workingConditionId, unitId }: StudyActions.DownloadPerformanceRequestLog
  ) {
    const request: RequestLogRequest = {
      studyUnitId: unitId,
      studyWorkingConditionId: workingConditionId,
      scope: StudyRequestLogScope.PERFORMANCES,
    };
    return this.requestLogService.getRequestLog(request);
  }

  @Action(StudyActions.AssignPerformanceAccessories)
  assignPerformanceAccessories(
    ctx: StateContext<string>,
    { performanceId, unitId, isTemporary, answers }: StudyActions.AssignPerformanceAccessories
  ) {
    const projectId = this.currentProjectId;
    const studyId = isTemporary ? this.currentTemporaryStudyId : this.currentStudyId;
    return this.studyService.assignPerformanceAccessories(projectId, studyId, performanceId, unitId, answers);
  }

  @Action(StudyActions.AssignMultiplePerformanceAccessories)
  assignMultiplePerformanceAccessories(
    ctx: StateContext<string>,
    { unitAnswers, isTemporary, requestTraceId }: StudyActions.AssignMultiplePerformanceAccessories
  ) {
    const projectId = this.currentProjectId;
    const studyId = isTemporary ? this.currentTemporaryStudyId : this.currentStudyId;
    return this.studyService.assignMultiplePerformanceAccessories(projectId, studyId, unitAnswers, requestTraceId);
  }

  @Action(StudyActions.DuplicateUnitAndAssignPerformanceAccessories)
  duplicateUnitAndAssignPerformanceAccessories(
    ctx: StateContext<string>,
    {
      performanceId,
      unitId,
      isTemporary,
      requestTraceId,
      answers,
    }: StudyActions.DuplicateUnitAndAssignPerformanceAccessories
  ) {
    const projectId = this.currentProjectId;
    const studyId = isTemporary ? this.currentTemporaryStudyId : this.currentStudyId;
    return this.studyService.duplicateUnitAndAssignPerformanceAccessories(
      projectId,
      studyId,
      performanceId,
      unitId,
      answers,
      requestTraceId
    );
  }

  @Action(StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories)
  duplicateUnitAndAssignMultiplePerformanceAccessories(
    ctx: StateContext<string>,
    { unitAnswers, isTemporary, requestTraceId }: StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories
  ) {
    const projectId = this.currentProjectId;
    const studyId = isTemporary ? this.currentTemporaryStudyId : this.currentStudyId;
    return this.studyService.duplicateUnitAndAssignMultiplePerformanceAccessories(
      projectId,
      studyId,
      unitAnswers,
      requestTraceId
    );
  }

  @Action(StudyActions.EditUnitConfiguration)
  editUnitConfiguration(
    ctx: StateContext<string>,
    { studyId, unitId, performanceId, workingConditionId, fromStudy }: StudyActions.EditUnitConfiguration
  ) {
    const projectId = this.currentProjectId;
    return this.studyService
      .editUnitConfiguration(projectId, studyId, unitId)
      .pipe(
        mergeMap(() =>
          ctx.dispatch(new StudyActions.GotoUnitConf(studyId, unitId, performanceId, workingConditionId, fromStudy))
        )
      );
  }

  @Action(StudyActions.AddExternalUnit)
  addExternalUnit(
    ctx: StateContext<string>,
    { bleenId, quantity, performanceId, workingConditions, requestTraceId }: StudyActions.AddExternalUnit
  ) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    const unitId = this.currentUnitId;

    return this.studyService.addExternalUnit({
      projectId,
      studyId,
      unitId,
      performanceId,
      bleenId,
      quantity,
      workingConditions,
      requestTraceId,
    });
  }

  @Action(StudyActions.UpdateCurrencyCode)
  updateCurrencyCode(ctx: StateContext<string>, { currencyCode, requestTraceId }: StudyActions.UpdateCurrencyCode) {
    this.domainDataService.bustPricingCache();
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;
    return this.studyService.updateCurrencyCode(projectId, studyId, currencyCode, requestTraceId);
  }

  @Action(StudyActions.UpgradeModels)
  upgradeModels(ctx: StateContext<string>, { requestTraceId }: StudyActions.UpgradeModels) {
    const projectId = this.currentProjectId;
    const studyId = this.currentStudyId;

    return this.studyService.upgradeModels(projectId, studyId, requestTraceId);
  }

  get currentProjectId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).projectId;
  }

  get currentStudyId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).studyId;
  }

  get currentTemporaryStudyId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).interimStudyId;
  }

  get currentUnitId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).unitId;
  }

  get currentWorkingConditionId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).workingConditionId;
  }
}
